import React from 'react';
import { formatDate } from '../utils/dateFormatter';
import {
  RELOCATION_BOOKED,
  RELOCATION_CURRENTLY_IN,
  RELOCATION_WILL_MOVE_TO,
  RELOCATION_PERIOD,
  RELOCATION_OUTSTANDING_AR
} from '../config/relocationLabels';
import '../styles/RelocationBanner.css';

/**
 * «محجوز: وحدة أ · الضيف الآن في: وحدة ب» — the guest is not where the booking says.
 * tasks/guest-relocation-v1.md §4.6 · consultant round 3 pulled this into stage 1.
 *
 * ── WHY THIS EXISTS AT ALL ────────────────────────────────────────────────────────────────────
 * Stage 1 records the move; the operational screens still resolve the unit from the booking until
 * stage 2 migrates them. The consultant's words for shipping without this: *"the database knows the
 * move happened, but the operators' tools tell them the guest is somewhere else"* — wrong unit
 * cleaned, wrong information given to the guest, a code cut on the wrong door. This banner is what
 * makes that half-state survivable, which is why it is not optional and not deferrable.
 *
 * ── DESIGN INTENT ─────────────────────────────────────────────────────────────────────────────
 * An instrument, not decoration. The operator reading it is usually standing up, often at 2am,
 * and needs one fact: which door do I actually go to.
 *
 *   - The two units are shown as a PAIR with the divergence made literal — the sold unit in muted
 *     ink, the real one in strong ink, joined by a directional glyph. A single sentence would make
 *     the reader parse; a pair makes them compare.
 *   - AMBER, not red and not the slate "on record" tint. Red is failure; slate is passive history.
 *     This is neither — it is a live contradiction the reader must hold in mind, which is attention.
 *   - The inline-start accent bar is the house pattern (GuestCheckoutLine, the readiness cards), so
 *     it reads as part of the family rather than an alien element.
 *   - Outstanding work gets a SECOND, quieter line. Mixing "where the guest is" with "the old code
 *     is unproven" in one sentence buries the second one, and the second one is the security fact.
 *
 * ── It states, it never instructs ─────────────────────────────────────────────────────────────
 * No buttons. Acting on a move happens where moves are managed; a control here would invite a press
 * from someone who opened the page for an unrelated reason.
 *
 * ── Arabic-only, like the rest of this page ───────────────────────────────────────────────────
 * Words live in `config/relocationLabels.js`, the same place `arrivalLabels` and
 * `guestRequestLabels` keep theirs — three surfaces will render this one fact, and agreeing on the
 * data while retyping the words is the failure that does not show up in a diff.
 *
 * ── `compact` — the SAME component on a list row ──────────────────────────────────────────────
 * The arrivals view and the inbox show this fact per row, where the full block would drown the row
 * it belongs to. Compact drops the dates and the outstanding line and keeps the one thing a row has
 * to carry: which unit the guest is actually in. It is a prop rather than a second component
 * because the wording and the tone must not be free to drift between surfaces — that is the whole
 * argument for the shared labels file, and a near-copy would defeat it.
 *
 * The outstanding work is deliberately NOT on the row: it is not actionable from a list, and three
 * rows each shouting about an unproven code turn the list into noise. It stays on the booking page.
 *
 * @param {Object|null} relocation  the backend's `currentRelocation` — null means the guest is in
 *                                  the unit they booked, which is a real answer, not "unknown".
 * @param {boolean} [compact]       one-line form for a list row
 */
const RelocationBanner = ({ relocation, className = '', compact = false }) => {
  // Null is "no move" — never render an empty shell that reads as missing data.
  if (!relocation || !relocation.currentUnit) return null;

  // ⚠️ TWO flips compose to make this point the right way, and only one of them is in this file.
  // The row is `display: flex` inside `dir="rtl"`, so the browser already reverses the visual order
  // — booked lands on the right, current on the left, and the eye travels LEFTWARD. The glyph is
  // then chosen to match; arrows are not bidi-mirrored, so it renders exactly as written.
  // If the CSS ever gains `row-reverse`, or drops flex for inline text, this inverts silently.
  const arrow = '←';

  // Only the facts a person can act on tonight. `verified` is done and `not_applicable` means there
  // was no code to kill; both stay off the line, because a warning that fires on a normal outcome
  // teaches people to stop reading it.
  //
  // ⚠️ `deferred` used to be excluded on the reasoning that a move which has not started has no
  // outstanding work. That was true only while something was expected to come back and do it —
  // nothing does. A deferral now survives ONLY when the guest is still in the original unit, which
  // means its code is live and a person has to kill it. Different fact, different sentence.
  const outstanding = [];
  if (relocation.oldCodeStatus === 'deferred') {
    outstanding.push(RELOCATION_OUTSTANDING_AR.oldCodeDeferred);
  } else if (relocation.oldCodeStatus === 'already_moved') {
    // Not «لم يتم تأكيد» — that claims a request was made. None was.
    outstanding.push(RELOCATION_OUTSTANDING_AR.oldCodeNotAttempted);
  } else if (relocation.oldCodeStatus && !['verified', 'not_applicable'].includes(relocation.oldCodeStatus)) {
    outstanding.push(RELOCATION_OUTSTANDING_AR.oldCodeUnverified);
  }

  // `role="alert"` is what this app uses for a static message banner (role="status" is reserved for
  // spinners here). It also announces on insert, which matters: this element does not exist until
  // the fetch resolves, and a polite live region inserted with its content usually says nothing.
  return (
    <div
      className={`relocation-banner ${compact ? 'relocation-banner--compact' : ''} ${className}`.trim()}
      dir="rtl"
      // A list row is not an alert. `role="alert"` interrupts a screen reader the moment the element
      // appears, which is right for one banner arriving on a page an operator is reading, and wrong
      // for thirty of them arriving at once when a list loads.
      role={compact ? undefined : 'alert'}
    >
      <div className="relocation-banner__units">
        <span className="relocation-banner__booked">
          <span className="relocation-banner__label">{RELOCATION_BOOKED}</span>
          <span className="relocation-banner__unit">{relocation.bookedUnit || '—'}</span>
        </span>

        <span className="relocation-banner__arrow" aria-hidden="true">{arrow}</span>

        <span className="relocation-banner__current">
          {/* Tense follows the fact. A move recorded before the guest arrives has not happened yet,
              and this banner's whole job is which door to go to RIGHT NOW. */}
          <span className="relocation-banner__label">
            {relocation.upcoming ? RELOCATION_WILL_MOVE_TO : RELOCATION_CURRENTLY_IN}
          </span>
          <span className="relocation-banner__unit">{relocation.currentUnit}</span>
        </span>
      </div>

      {!compact && relocation.from && relocation.to && (
        <div className="relocation-banner__dates">
          {/* Business dates through the central formatter — never a local one, and never raw ISO on
              a surface an operator reads under pressure. Shown check-in → check-out exactly as a
              booking's own dates are shown on this page, so the two read the same way. */}
          {RELOCATION_PERIOD} {formatDate(relocation.from)}
          {' — '}
          {formatDate(relocation.to)}
        </div>
      )}

      {!compact && outstanding.length > 0 && (
        <div className="relocation-banner__outstanding">
          {outstanding.map((item) => (
            <span key={item} className="relocation-banner__outstanding-item">{item}</span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RelocationBanner;
